import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { object, string, ref } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { changeUser, fetchUserInfo, resetStatus } from "../redux/usersSagas";
import { MainModal } from "../../../../components/modal/MainModal";
import LoadingAnimation from "../../../../components/loadingAnimation/loadingAnimation";
import { Permissions } from "./Permissions";
import { UserInfo } from "./UserInfo";
import { ErrorList } from "./ErrorList";
import { useError } from "../hooks/useError";
import { showToast } from "../../../../utils/showToast";
import { passwordSchemaTest } from "../../../../utils/password-utils";

const schema = object({
  first_name: string().label("First Name").min(2).required(),
  last_name: string().label("Last Name").min(2).required(),
  email: string()
    .label("Email")
    .when({
      is: (value) => !!value,
      then: (schema) => schema.email()
    }),
  password: string()
    .label("Password")
    .when({ is: (value) => !!value, then: (schema) => schema.min(8).test(passwordSchemaTest) }),
  password_confirmation: string()
    .label("Password Confirmation")
    .when({
      is: (value) => !!value,
      then: (schema) => schema.oneOf([ref("password")], "${path} must match Password").min(8)
    }),
  role_id: object().when({
    is: (value) => !!value,
    then: (schema) => schema.required("Please choose role")
  })
}).required();

export const Edit = ({
  isOpen,
  onClose,
  selected: {
    isMultiOrganizations,
    userActions: { canEditUser, canUpdatePermissions },
    id
  }
}) => {
  const dispatch = useDispatch();
  const currentUserInfo = useSelector((state) => state.users.currentUserInfo);
  const { roles, permissions, locations } = useSelector((state) => state.users.commonSettings);

  const {
    firstName,
    lastName,
    email,
    roleId,
    phone,
    enabled2fa,
    disabled2faByEmail,
    daysInterval2fa,
    permissionIds = [],
    sendSeparateDailyReports,
    sendInactiveUsersEmail,
    clubreadyStoreIds = [],
    corporateReportIds = [],
    user_actions: {
      canChangeUserEmail,
      canChange2FA,
      canChangeRole,
      canUpdateLocations,
      canChangeEmails,
      canUpdateCorporateReports,
      canSendInactiveUsersEmail,
      canSendLocationComparisonDailyEmail
    } = {}
  } = currentUserInfo || {};

  const selectedRole = roles.find((role) => role.id == roleId);

  const defaultValues = {
    first_name: firstName,
    last_name: lastName,
    email,
    role_id: selectedRole,
    phone_number: phone,
    enabled_2fa: enabled2fa,
    disabled_2fa_by_email: disabled2faByEmail,
    days_interval_2fa: daysInterval2fa,
    permission_ids: permissionIds.reduce((acc, val) => ({ ...acc, [val]: true }), {}),
    clubready_store_ids: clubreadyStoreIds.reduce((acc, val) => ({ ...acc, [val]: true }), {}),
    corporate_report_ids: corporateReportIds.reduce((acc, val) => ({ ...acc, [val]: true }), {}),
    send_separate_daily_reports: sendSeparateDailyReports,
    send_inactive_users_email: sendInactiveUsersEmail,
    password: "",
    password_confirmation: ""
  };

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, dirtyFields, isDirty }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
    values: defaultValues
  });

  const [activeTab, setActiveTab] = useState(0);

  const close = () => {
    dispatch(resetStatus());
    onClose();
  };

  useEffect(() => {
    dispatch(fetchUserInfo(id));
  }, []);

  const { errors: serverErrors, status } = useError(() => {
    showToast("User was successfully updated");
    close();
  });

  const primaryButtonDisabled = !isDirty || status == "pending";

  const onSubmit = (data) => {
    let changedFieldKeys = Object.keys(dirtyFields);
    let formattedDataForRequest = {};

    changedFieldKeys.forEach((key) => {
      if (
        key === "clubready_store_ids" ||
        key == "permission_ids" ||
        key == "corporate_report_ids"
      ) {
        formattedDataForRequest = {
          ...formattedDataForRequest,
          [key]: Object.keys(data[key]).map((id) => parseInt(id))
        };
      } else if (key === "role_id") {
        formattedDataForRequest = {
          ...formattedDataForRequest,
          [key]: data[key].id
        };
      } else {
        formattedDataForRequest = { ...formattedDataForRequest, [key]: data[key] };
      }
    });

    dispatch(changeUser({ data: formattedDataForRequest, id }));
  };

  const activeTabClassName = "tabs__tab--active";
  const allPermissions = {
    location_permissions: locations,
    ...permissions
  };

  const renderPermissions = () => (
    <Permissions
      watch={watch}
      setValue={setValue}
      allPermissions={allPermissions}
      control={control}
      register={register}
      canUpdateLocations={canUpdateLocations}
      canChangeEmails={canChangeEmails}
      canUpdateCorporateReports={canUpdateCorporateReports}
      canSendInactiveUsersEmail={canSendInactiveUsersEmail}
      canSendLocationComparisonDailyEmail={canSendLocationComparisonDailyEmail}
    />
  );

  const renderUserInfo = () => (
    <UserInfo
      register={register}
      control={control}
      roles={roles}
      canChange2FA={canChange2FA}
      canChangeRole={canChangeRole}
      disableRole={isMultiOrganizations}
      selectedRole={selectedRole}
      canChangeUserEmail={canChangeUserEmail}
    />
  );

  return (
    <>
      <MainModal
        isLoading={!currentUserInfo}
        parentSelector=".users"
        isOpen={isOpen}
        onClose={close}
        headerText={
          <>
            Edit User -{" "}
            <span className="text-bold">
              {lastName}, {firstName}
            </span>
          </>
        }
        headerCloseHandler={close}
        height="auto"
        width={900}
        primaryButtonText="Save"
        primaryBtnHandler={handleSubmit(onSubmit)}
        primaryButtonClass="primary"
        primaryButtonDisabled={primaryButtonDisabled}
        secondaryBtnText="Cancel"
        secondaryBtnHandler={close}
        secondaryBtnClass="secondary"
        containerClassName="edit"
        isAdminPanel={false}
      >
        {canEditUser && canUpdatePermissions && (
          <div className="tabs">
            <div className="tabs__list">
              <button
                className={`tabs__tab ${activeTab === 0 ? activeTabClassName : ""}`}
                onClick={() => setActiveTab(0)}
              >
                User Info
              </button>
              <button
                className={`tabs__tab ${activeTab === 1 ? activeTabClassName : ""}`}
                onClick={() => setActiveTab(1)}
              >
                Permissions
              </button>
            </div>

            <form id="hook-form">{activeTab === 0 ? renderUserInfo() : renderPermissions()}</form>
          </div>
        )}
        {canEditUser && !canUpdatePermissions && renderUserInfo()}
        {canUpdatePermissions && !canEditUser && renderPermissions()}
        {status === "pending" && <LoadingAnimation uiLoader />}
        <ErrorList errors={{ ...errors, ...serverErrors }} />
      </MainModal>
    </>
  );
};
